// Spec #34101 (F1c) - calls the stateless POST /api/v1/intake-intelligence/
// patient-search and /requestor-search endpoints. Keys travel in the POST
// body (never the query string/URL - AC18); the same functions serve both
// the automated match (extracted keys) and the manual modal re-search
// (specialist-typed keys), per AC4.
import { get, patchRequest, post } from "../../../services/Network";
import { PatientMatchApiPayload, RequestorMatchApiPayload } from "../matchState";
import { Release } from "../../../models/transaction";
import { Contact } from "../../../models/misc";

export interface PatientSearchRequestBody {
    readonly facilityId: number;
    readonly userId: number;
    readonly lastName?: string;
    readonly dob?: string;
    readonly mrn?: string;
}

export interface RequestorSearchRequestBody {
    readonly facilityId: number;
    readonly userId: number;
    readonly fax?: string;
    readonly email?: string;
    readonly name?: string;
    readonly org?: string;
    /**
     * AB#35289 (AC5): optional requestor-TYPE filter driven by the LIVE-selected
     * request type (5005 = Internal). Omitted for non-Internal requests, leaving
     * the match search unfiltered - the backend param defaults to null.
     */
    readonly typeOfRequestorID?: number;
    // Spec #35136 (F1f): DDS matches on the barcode's RQ site code instead of fax/email.
    readonly siteCode?: string;
}

export function patientSearchApi(request: PatientSearchRequestBody) {
    return post<PatientMatchApiPayload>("v1/intake-intelligence/patient-search", request);
}

export function requestorSearchApi(request: RequestorSearchRequestBody) {
    return post<RequestorMatchApiPayload>("v1/intake-intelligence/requestor-search", request);
}

/**
 * AB#39052 (Ali Moll, PR !10513 thread 56655) - the contact demographics for ONE
 * contact, by id.
 *
 * The requestor search is requestor-level: it returns `contactId` and a count of how
 * many contacts matched, and no first/last name, email, phone or fax. Those values
 * could not differentiate candidates that matched BECAUSE they share the searched fax
 * or email, and the fax in particular misrepresented the result (a contact matches on
 * its PHONE too, and 235,155 active contacts on aspTest have a phone whose fax differs
 * or is empty). So they are read here instead, for the ONE contact the row resolves to.
 *
 * `GET api/requestors/{contactId}/contact` -> ROIS_RequestorContactByContactID_sel, a
 * single-row primary-key lookup. Direct call rather than a Redux saga, the established
 * pattern for Quick Entry above, and it still routes through services/Network so it
 * carries withCredentials and the shared auth/cache headers. No PHI in the URL - the
 * path carries a contact id and nothing else.
 */
export async function requestorContactApi(contactId: number): Promise<Contact | undefined> {
    const response = await get<Contact>(`requestors/${contactId}/contact`);
    return response.data ?? undefined;
}

// Spec #34102 (F1d), AB#34102 - records the Quick Entry -> Classic Mode switch
// as the intake-timing "switch" event (AuditTask.ExitToClassicIntake = 812).
// Fire-and-forget: the caller must ignore failures so the switch is never
// blocked by the audit write. transactionId is "{facilityId}-{requestBufferId}".
export function exitToClassicIntakeApi(transactionId: string, userId: number) {
    return post(`transactions/${transactionId}/intake/exitToClassic?userId=${userId}`, {});
}

// AB#35086 - records the intake-timing "session started" event
// (AuditTask.QuickEntrySessionStarted = 814) for a genuinely first extraction on
// this transaction. Fire-and-forget, same shape as exitToClassicIntakeApi above.
export function quickEntrySessionStartedApi(transactionId: string, userId: number) {
    return post(`transactions/${transactionId}/intake/sessionStarted?userId=${userId}`, {});
}

/**
 * AB#31605 - the record types that WOULD apply under `requestPurposeId`, computed
 * server-side WITHOUT persisting that purpose (read-only GET; the underlying
 * ROIS_InformationToRelease_sel performs no writes).
 *
 * This is what lets Quick Entry keep an unconfirmed Request Purpose in local
 * component state like every other Quick Entry field. Before this endpoint the
 * only way to learn the applicable record types was to POST transactions/purpose
 * first - and that write runs ROIS_spUTransactionRequestPurpose_upd, which for
 * purpose 43 (Disclosure Event) migrates the transaction into tblRPQ and DELETEs
 * its tblRequestBuffer row. A dropdown selection performed a one-way transition.
 *
 * transactionId is "{facilityId}-{requestBufferId}".
 */
export async function releaseInfoPreviewApi(
    transactionId: string,
    userId: number,
    requestPurposeId: number,
): Promise<readonly Release[]> {
    const response = await get<readonly Release[]>(
        `transactions/${transactionId}/informationToRelease?userId=${userId}&requestPurposeId=${requestPurposeId}`,
    );
    return coerceReleaseServiceDates(response.data ?? []);
}

/**
 * AB#35135 follow-up - select or deselect ONE PHI code on a transaction.
 * `PATCH transactions/{id}/phidisclosed?requestedItemid=N&delete=bool`.
 *
 * Called DIRECTLY rather than through `updatePHIDisclosed`'s Redux saga, and that is
 * deliberate: the saga is registered with `takeLatest`
 * (services/sagas/index.js - UPDATE_PHI_DISCLOSED_SAGA), which CANCELS the in-flight
 * run whenever another action of the same type is dispatched. Quick Entry's
 * Confirm-All gate emits one call per CHANGED code, so going through the saga would
 * silently drop every write except the last. Classic is unaffected - it dispatches
 * these one at a time from individual checkbox clicks, where takeLatest is harmless.
 *
 * Direct calls from Quick Entry are the established pattern here (see the search
 * functions above and the Confirm-All purpose write), and this still routes through
 * services/Network so it carries withCredentials + the shared auth/cache headers
 * (security.md SSRF - no parallel HTTP path).
 *
 * transactionId is "{facilityId}-{requestBufferId}". Contains no PHI in the URL: the
 * query carries a catalog item id and a boolean, never a code value or description.
 */
export function phiDisclosedApi(
    transactionId: string,
    userId: number,
    requestedItemID: number,
    isDelete: boolean,
) {
    return patchRequest(
        `transactions/${transactionId}/phidisclosed?requestedItemid=${requestedItemID}&userID=${userId}&delete=${isDelete}`,
        {},
    );
}

/**
 * Re-hydrates `dateOfService.from/to` from the JSON's ISO strings to real `Date`s,
 * matching EXACTLY what the Redux reducer does for `transactionDetails.releaseInfo`
 * on getTransactionDetailsSuccess (DataEntryDetailsDucks.ts).
 *
 * The preview deliberately does not round-trip through that reducer - it feeds
 * Quick Entry's local state - so without this the preview's items would carry raw
 * strings while locally-edited items carry real Dates, i.e. a mixed-shape list.
 * `getReleaseDateRange`'s own `coerceDate` (RecordsRequestedField.tsx) is already
 * defensive about that, but `infoToReleaseValidiation` in localValidations.ts also
 * reads `dateOfService` directly, so keeping the two paths shape-identical is the
 * conservative choice rather than auditing every consumer for string-tolerance.
 *
 * Lives inside the api function rather than at the call site so it cannot be
 * forgotten - a raw string reaching formatServiceDate throws at runtime, which has
 * happened before (see RecordsRequestedField.tsx's note on that report).
 *
 * The cast is deliberate and narrow: `DateRange` (models/misc.ts) declares
 * `from: Date` and `to: Date` as REQUIRED, but a half-open range is real - the
 * reducer produces `{from: Date|undefined, to: Date|undefined}` too and only avoids
 * this error because its `action.data` is untyped `any`. Modelling that honestly is
 * a wider change than this fix; the cast keeps the preview faithful to existing
 * runtime behavior instead of silently dropping half-open ranges.
 */
function coerceReleaseServiceDates(items: readonly Release[]): readonly Release[] {
    return items.map(item => ({
        ...item,
        dateOfService: (item.dateOfService
            ? {
                from: item.dateOfService.from ? new Date(item.dateOfService.from) : undefined,
                to: item.dateOfService.to ? new Date(item.dateOfService.to) : undefined,
            }
            : undefined) as Release["dateOfService"],
    }));
}
